import { useMemo } from 'react';
import { ColumnGroup } from 'react-table';

import { SizeScale, SizeScaleEntry } from 'types/api/sizeScales.type';

import { InactiveSize } from 'pages/Catalogue/components/SizeScales/SizeScales.styles';

// A scale's sizes in display order (by Indice), inactive ones included
const ordered = (scale: SizeScale) =>
  [...(scale.entries ?? [])].sort((a, b) => a.sortIndex - b.sortIndex);

// Column N is the N-th size of a scale in display order (no position is stored: a size's column
// moves when a size is added before it). As many columns as the longest scale has sizes; the
// table scrolls sideways.
export const useColumns = (scales: SizeScale[]) => {
  // each scale sorted once, not in every cell (the table's rows are these same scale objects)
  const sortedEntries = useMemo(
    () => new Map(scales.map((scale) => [scale, ordered(scale)])),
    [scales]
  );
  const columnCount = Math.max(0, ...Array.from(sortedEntries.values(), (e) => e.length));

  return useMemo(
    (): ColumnGroup[] => [
      {
        Header: 'Size scales',
        columns: [
          {
            Header: 'Scale',
            accessor: 'scaleCode',
          },
          ...Array.from({ length: columnCount }, (_, i) => i + 1).map((column) => ({
            Header: String(column),
            id: `size${column}`,
            accessor: (scale: {}) => sortedEntries.get(scale as SizeScale)?.[column - 1],
            Cell: ({ value }: { value?: SizeScaleEntry }) => {
              if (!value) return null;
              if (value.active) {
                return <span title={`Indice ${value.sortIndex}`}>{value.label}</span>;
              }

              return (
                <InactiveSize
                  title={`Indice ${value.sortIndex}: no longer in the scale, kept for the order lines that hold it`}
                >
                  {value.label}
                </InactiveSize>
              );
            },
          })),
          {
            Header: 'Used in orders',
            accessor: 'usedInOrders',
            Cell: ({ value }: { value: boolean }) => <span>{value ? 'Yes' : ''}</span>,
          },
        ],
      },
    ],
    [columnCount, sortedEntries]
  );
};
